import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, MoreHorizontal, XCircle } from "lucide-react";
import ToastHelper from "@/utils/toastHelper";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { adminEnrolmentService } from "@/services/adminEnrolmentService";
import { certificateService } from "@/services/certificateService";
import {
  EnrollmentAction,
  EnrollmentActionStatusMap,
  EnrollmentStatus,
  EnrollmentStatusMeta,
  getStatusMeta,
} from "@/utils/enrolmentStatus";
import StudentEnrollmentDocuments from "@/pages/student/enrollment/StudentEnrollmentDocuments";

const TAB_CONFIG = [
  { key: "All", label: "All", service: "getAllEnrolments" },
  ...Object.entries(EnrollmentStatusMeta).filter(([id]) => Number(id) > 0).map(([id, meta]) => ({ key: meta.label, label: meta.label, status: Number(id) })),
];

const ACTION_STATUS_MAP = EnrollmentActionStatusMap;

const getAllowedActions = (status) => {
  const actions = {
    [EnrollmentStatus.Submitted]: [
      EnrollmentAction.Approve,
      EnrollmentAction.RequestDocuments,
      EnrollmentAction.Return,
      EnrollmentAction.Reject,
    ],
    [EnrollmentStatus.DocumentsPending]: [
      EnrollmentAction.Approve,
      EnrollmentAction.Return,
    ],
    [EnrollmentStatus.PaymentPending]: [EnrollmentAction.ConfirmPayment],
    [EnrollmentStatus.PaymentReceived]: [
      EnrollmentAction.Approve,
      EnrollmentAction.Return,
      EnrollmentAction.Reject,
    ],
    [EnrollmentStatus.Approved]: [
      EnrollmentAction.Book,
      EnrollmentAction.Complete,
    ],
    [EnrollmentStatus.Booked]: [EnrollmentAction.Complete],
    [EnrollmentStatus.InProgress]: [EnrollmentAction.Complete],
    [EnrollmentStatus.Completed]: [EnrollmentAction.IssueCertificate],
  }[Number(status)] || [];

  const canClose =
    Number(status) > EnrollmentStatus.None &&
    Number(status) < EnrollmentStatus.Completed;

  return canClose
    ? [...actions, EnrollmentAction.Cancel, EnrollmentAction.Expire]
    : actions;
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBoolean = (value) => {
  if (value === true) {
    return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />Yes</span>;
  }

  if (value === false) {
    return <span className="inline-flex items-center gap-1 text-slate-500"><XCircle className="h-4 w-4" />No</span>;
  }

  return "-";
};

const normalizeEnrolments = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.enrolments ?? payload?.data ?? payload?.result ?? payload?.items ?? [];

  return list.map((item) => ({
    id: item.studentEnrollmentId ?? item.studentEnrollmentID ?? item.id,
    studentEnrollmentId: item.studentEnrollmentId ?? item.studentEnrollmentID ?? item.id,
    studentBookingId: item.studentBookingId ?? item.studentBookingID,
    studentId: item.studentId ?? item.studentID,
    bookingNumber: item.bookingNumber ?? item.bookingNo ?? "-",
    studentName: item.studentName ?? "Unknown",
    email: item.email ?? "-",
    mobileNumber: item.mobileNumber ?? item.mobile ?? "-",
    courseName: item.courseName ?? "-",
    campusName: item.campusName ?? "-",
    scheduleDate: formatDate(item.scheduleDate),
    submittedDate: formatDate(item.submittedDate),
    progressPercentage: item.progressPercentage ?? 0,
    currentStep: item.currentStep ?? "-",
    usiNumber: item.usiNumber ?? "-",
    usiVerified: item.usiVerified ?? false,
    documentsVerified: item.documentsVerified ?? false,
    enrolmentStatus: item.enrolmentStatus ?? item.status,
    statusLabel: getStatusMeta(item.enrolmentStatus ?? item.status).label,
  }));
};

const ProgressIndicator = ({ percentage, step }) => {
  const value = Math.max(0, Math.min(100, Number(percentage) || 0));
  return <div className="min-w-32"><div className="mb-1 flex justify-between text-xs"><span className="font-semibold text-slate-700">{value}%</span><span className="max-w-20 truncate text-slate-500" title={step}>{step || "Not started"}</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${value === 100 ? "bg-emerald-500" : "bg-orange-500"}`} style={{ width: `${value}%` }} /></div></div>;
};

export default function AdminEnrolments() {
  const [enrolments, setEnrolments] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [processingId, setProcessingId] = useState(null);
  const [selectedEnrolments, setSelectedEnrolments] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [certificateDialog, setCertificateDialog] = useState(null);

  const viewEnrolment = async (enrolment) => {
    setDetailLoading(true);
    try {
      const payload = await adminEnrolmentService.getEnrolmentDetail(enrolment.studentBookingId);
      setDetail({
        summary: enrolment,
        enrollment:
          payload?.enrollment ||
          payload?.studentEnrollment ||
          payload?.Enrollment ||
          payload?.StudentEnrollment ||
          payload,
        booking: payload?.booking || payload?.Booking || {},
        documents:
          payload?.documents ||
          payload?.studentDocuments ||
          payload?.Documents ||
          payload?.StudentDocuments ||
          [],
      });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to load enrolment details");
    } finally { setDetailLoading(false); }
  };

  const fetchEnrolments = async (tabKey) => {
    setLoading(true);
    setActiveTab(tabKey);

    try {
      const selectedTab = TAB_CONFIG.find((tab) => tab.key === tabKey) ?? TAB_CONFIG[0];
      const response = selectedTab.status
        ? await adminEnrolmentService.getEnrolmentsByStatus(selectedTab.status)
        : await adminEnrolmentService.getAllEnrolments();
      const nextEnrolments = normalizeEnrolments(response);
      setEnrolments(nextEnrolments);
      setTotalRecords(typeof response?.totalRecords === "number" ? response.totalRecords : nextEnrolments.length);
      setSelectedEnrolments([]);
    } catch (error) {
      console.error("Failed to load enrolments", error);
      setEnrolments([]);
      setTotalRecords(0);
      setSelectedEnrolments([]);
      ToastHelper.apiError(error, "Unable to load enrolments");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (enrolment) => {
    setSelectedEnrolments((current) => {
      const id = enrolment.studentEnrollmentId ?? enrolment.id;
      const exists = current.some((item) => item.enrolmentId === id);

      if (exists) {
        return current.filter((item) => item.enrolmentId !== id);
      }

      return [...current, { enrolmentId: id, studentName: enrolment.studentName }];
    });
  };

  const toggleSelectAll = () => {
    if (selectedEnrolments.length === enrolments.length) {
      setSelectedEnrolments([]);
      return;
    }

    setSelectedEnrolments(
      enrolments.map((enrolment) => ({
        enrolmentId: enrolment.studentEnrollmentId ?? enrolment.id,
        studentName: enrolment.studentName,
      }))
    );
  };

  const handleAction = async (enrolment, action, adminComments = "") => {
    if (!enrolment?.studentEnrollmentId) {
      ToastHelper.error("Unable to process this enrolment because the ID is missing.");
      return;
    }

    setProcessingId(enrolment.studentEnrollmentId);

    try {
      const payload = {
        studentEnrollmentId: enrolment.studentEnrollmentId,
        action,
        status: ACTION_STATUS_MAP[action] ?? null,
        adminComments,
        rejectedReason: action === "Reject" ? adminComments : "",
      };

      await adminEnrolmentService.performEnrolmentAction(payload);
      ToastHelper.success(`${action} action completed for ${enrolment.studentName}`);
      await fetchEnrolments(activeTab);
      setActionDialog(null);
    } catch (error) {
      console.error("Failed to update enrolment", error);
      ToastHelper.apiError(error, `Unable to ${action.toLowerCase()} enrolment`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleExportCsvAsqa = async () => {
    try {
      const response = await adminEnrolmentService.exportCsvAsqa({
        selectedRecords: selectedEnrolments.length > 0
          ? selectedEnrolments.map((item) => ({
              studentEnrollmentId: item.enrolmentId,
              studentId: item.studentId ?? 0,
            }))
          : [
              {
                studentEnrollmentId: 0,
                studentId: 0,
              },
            ],
        status: activeTab,
      });

      const disposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"] || "";
      const fileName = disposition.split("filename=")[1]?.replace(/"/g, "") || `enrolments-asqa-${Date.now()}.csv`;
      const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      ToastHelper.success("ASQA CSV export downloaded");
    } catch (error) {
      console.error("Failed to export ASQA CSV", error);
      ToastHelper.apiError(error, "Unable to export ASQA CSV");
    }
  };

  useEffect(() => {
    fetchEnrolments("All");
  }, []);

  const summaryText = useMemo(() => {
    if (loading) return "Loading enrolments…";
    return `${totalRecords} record${totalRecords === 1 ? "" : "s"}`;
  }, [loading, totalRecords]);

  const overview = useMemo(() => ({
    review: enrolments.filter((item) => [EnrollmentStatus.Submitted, EnrollmentStatus.Returned, EnrollmentStatus.DocumentsPending, EnrollmentStatus.PaymentReceived].includes(Number(item.enrolmentStatus))).length,
    approved: enrolments.filter((item) => Number(item.enrolmentStatus) === EnrollmentStatus.Approved).length,
    completed: enrolments.filter((item) => [EnrollmentStatus.Completed, EnrollmentStatus.CertificateIssued].includes(Number(item.enrolmentStatus))).length,
  }), [enrolments]);

  return (
    <div className="space-y-5" data-testid="enrolments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Enrolments</h1>
          <div className="text-sm text-slate-500">Review and manage enrolment progression across all statuses.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleExportCsvAsqa} className="wc-btn-outline text-xs" data-testid="enrolments-export-csv">Export to CSV (ASQA)</button>

          <button onClick={() => ToastHelper.success(`Exported ${enrolments.length} enrolments to Excel`)} className="wc-btn-outline text-xs" data-testid="enrolments-export-csv">Export Excel</button>
          <button onClick={() => ToastHelper.success(`Exported ${enrolments.length} enrolments to PDF`)} className="wc-btn-outline text-xs" data-testid="enrolments-export-pdf">Export PDF</button>
          <button onClick={() => ToastHelper.success(`Exported ${enrolments.length} enrolments to RTO`)} className="wc-btn-outline text-xs" data-testid="enrolments-export-rto">Export to RTO</button>
          <div className="text-sm font-medium text-slate-600">{summaryText}</div>
        </div>
      </div>

      <div className="wc-card p-4">
        <div className="flex flex-wrap gap-2">
          {TAB_CONFIG.map((tab) => (
            <Button
              key={tab.key}
              type="button"
              variant={activeTab === tab.key ? "default" : "outline"}
              size="sm"
              onClick={() => fetchEnrolments(tab.key)}
              disabled={loading}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[{ label: "Total enrolments", value: totalRecords, style: "text-[#0A2540]" }, { label: "Needs review", value: overview.review, style: "text-amber-700" }, { label: "Approved", value: overview.approved, style: "text-emerald-700" }, { label: "Completed / certified", value: overview.completed, style: "text-teal-700" }].map((item) => <div key={item.label} className="wc-card py-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</div><div className={`mt-1 text-2xl font-display font-extrabold ${item.style}`}>{item.value}</div></div>)}
      </div>

      <div className="wc-card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading enrolments...
          </div>
        ) : enrolments.length === 0 ? (
          <div className="px-4 py-16 text-center text-slate-400">No enrolments found for this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={enrolments.length > 0 && selectedEnrolments.length === enrolments.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left">Booking Number</th>
                  <th className="px-4 py-3 text-left">Student Name</th>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Campus</th>
                  <th className="px-4 py-3 text-left">Schedule Date</th>
                  <th className="px-4 py-3 text-left">Progress</th>
                  <th className="px-4 py-3 text-left">Compliance checks</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enrolments.map((enrolment) => {
                  const enrolmentId = enrolment.studentEnrollmentId ?? enrolment.id;
                  const isSelected = selectedEnrolments.some((item) => item.enrolmentId === enrolmentId);

                  return (
                    <tr key={enrolment.id} className="hover:bg-slate-50 align-top">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(enrolment)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                      </td>
                    <td className="px-4 py-3">{enrolment.bookingNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{enrolment.studentName}</div>
                      <div className="text-xs text-slate-500">{enrolment.email}</div>
                    </td>
                    <td className="px-4 py-3">{enrolment.courseName}</td>
                    <td className="px-4 py-3">{enrolment.campusName}</td>
                    <td className="px-4 py-3">{enrolment.scheduleDate}</td>
                    <td className="px-4 py-3"><ProgressIndicator percentage={enrolment.progressPercentage} step={enrolment.currentStep} /></td>
                    <td className="px-4 py-3"><div className="space-y-1 text-xs"><div>USI: {formatBoolean(enrolment.usiVerified)}</div><div>Documents: {formatBoolean(enrolment.documentsVerified)}</div></div></td>
                    <td className="px-4 py-3">{enrolment.submittedDate}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusMeta(enrolment.enrolmentStatus).className}`}>
                        {enrolment.statusLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" disabled={processingId === enrolment.studentEnrollmentId}>
                            {processingId === enrolment.studentEnrollmentId ? "Working..." : <><MoreHorizontal className="mr-1 h-3.5 w-3.5" />Actions</>}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewEnrolment(enrolment)}>View</DropdownMenuItem>
                          {getAllowedActions(enrolment.enrolmentStatus).map((action) => (
                            <DropdownMenuItem
                              key={action}
                              onClick={() => {
                                if (action === "Issue Certificate") {
                                  setCertificateDialog(enrolment);
                                  return;
                                }
                                setActionDialog({ enrolment, action, comments: "" });
                              }}
                            >
                              {action}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detailLoading && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40"><div className="rounded-lg bg-white px-6 py-4 shadow-xl flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading enrolment...</div></div>}
      {detail && <EnrolmentDetailDialog detail={detail} onClose={() => setDetail(null)} />}
      {actionDialog && <EnrolmentActionDialog value={actionDialog} onChange={setActionDialog} onClose={() => setActionDialog(null)} onConfirm={() => handleAction(actionDialog.enrolment, actionDialog.action, actionDialog.comments)} processing={processingId === actionDialog.enrolment.studentEnrollmentId} />}
      {certificateDialog && (
        <IssueCertificateDialog
          enrolment={certificateDialog}
          onClose={() => setCertificateDialog(null)}
          onIssued={async () => {
            setCertificateDialog(null);
            await fetchEnrolments(activeTab);
          }}
        />
      )}
    </div>
  );
}

function IssueCertificateDialog({ enrolment, onClose, onIssued }) {
  const [form, setForm] = useState({
    certificateNumber: "",
    certificateName: enrolment.courseName && enrolment.courseName !== "-"
      ? `${enrolment.courseName} Certificate`
      : "Certificate",
    statementOfAttainmentNumber: "",
    issueDate: new Date().toISOString().slice(0, 10),
    expiryDate: "",
    remarks: "",
    documentDescription: "Issued from Admin Enrolments",
    file: null,
  });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    if (!form.certificateNumber.trim()) {
      ToastHelper.error("Certificate number is required.");
      return;
    }
    if (!form.certificateName.trim()) {
      ToastHelper.error("Certificate name is required.");
      return;
    }
    if (!form.issueDate) {
      ToastHelper.error("Issue date is required.");
      return;
    }
    if (!form.file) {
      ToastHelper.error("Certificate PDF is required.");
      return;
    }
    if (form.file.type && form.file.type !== "application/pdf") {
      ToastHelper.error("Please select a PDF certificate file.");
      return;
    }
    if (form.file.size > 10 * 1024 * 1024) {
      ToastHelper.error("Certificate PDF must not exceed 10 MB.");
      return;
    }

    const data = new FormData();
    data.append("EnrollmentId", String(enrolment.studentEnrollmentId));
    data.append("CertificateNumber", form.certificateNumber.trim());
    data.append("CertificateName", form.certificateName.trim());
    data.append("IssueDate", new Date(`${form.issueDate}T00:00:00`).toISOString());
    if (form.expiryDate) data.append("ExpiryDate", new Date(`${form.expiryDate}T00:00:00`).toISOString());
    if (form.statementOfAttainmentNumber.trim()) data.append("StatementOfAttainmentNumber", form.statementOfAttainmentNumber.trim());
    if (form.remarks.trim()) data.append("Remarks", form.remarks.trim());
    if (form.documentDescription.trim()) data.append("DocumentDescription", form.documentDescription.trim());
    data.append("File", form.file);

    setSaving(true);
    try {
      const response = await certificateService.create(data);
      ToastHelper.success(response?.message || "Certificate issued successfully.");
      await onIssued();
    } catch (error) {
      ToastHelper.apiError(error, "Unable to issue certificate.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <div className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-display font-extrabold text-[#0A2540]">Issue certificate</h2>
          <p className="mt-1 text-sm text-slate-500">{enrolment.studentName} · {enrolment.courseName} · {enrolment.bookingNumber}</p>
        </div>

        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate number *</span>
            <input className="wc-input" value={form.certificateNumber} onChange={(event) => set("certificateNumber", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate name *</span>
            <input className="wc-input" value={form.certificateName} onChange={(event) => set("certificateName", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Statement of Attainment number</span>
            <input className="wc-input" value={form.statementOfAttainmentNumber} onChange={(event) => set("statementOfAttainmentNumber", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Issue date *</span>
            <input type="date" className="wc-input" value={form.issueDate} onChange={(event) => set("issueDate", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Expiry date</span>
            <input type="date" className="wc-input" value={form.expiryDate} onChange={(event) => set("expiryDate", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate PDF *</span>
            <input type="file" accept="application/pdf,.pdf" className="wc-input" onChange={(event) => set("file", event.target.files?.[0] || null)} />
            <small className="text-slate-500">PDF only, maximum 10 MB.</small>
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Document description</span>
            <input className="wc-input" value={form.documentDescription} onChange={(event) => set("documentDescription", event.target.value)} />
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Remarks</span>
            <textarea className="wc-input min-h-24" value={form.remarks} onChange={(event) => set("remarks", event.target.value)} />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button className="wc-btn-outline" disabled={saving} onClick={onClose}>Cancel</button>
          <button className="wc-btn-primary" disabled={saving} onClick={submit}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Issuing..." : "Issue certificate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EnrolmentActionDialog({ value, onChange, onClose, onConfirm, processing }) {
  const needsReason = value.action === "Reject" || value.action === "Return";
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4"><div className="w-full max-w-lg rounded-xl bg-white shadow-2xl"><div className="border-b px-6 py-4"><h2 className="text-xl font-extrabold text-[#0A2540]">{value.action} enrolment</h2><p className="text-sm text-slate-500">{value.enrolment.studentName} · {value.enrolment.bookingNumber}</p></div><div className="p-6"><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{needsReason ? "Reason / comments" : "Admin comments"}</span><textarea autoFocus className="wc-input min-h-28" value={value.comments} onChange={e=>onChange({...value,comments:e.target.value})} placeholder={needsReason ? `Enter the reason for ${value.action.toLowerCase()}ing this enrolment` : "Optional approval comments"} /></label>{needsReason && !value.comments.trim() && <p className="mt-2 text-xs text-amber-700">A reason is required.</p>}</div><div className="flex justify-end gap-2 border-t px-6 py-4"><button className="wc-btn-outline" onClick={onClose}>Cancel</button><button className="wc-btn-primary" disabled={processing || (needsReason && !value.comments.trim())} onClick={onConfirm}>{processing ? "Saving..." : `Confirm ${value.action}`}</button></div></div></div>;
}

const DETAIL_SECTIONS = [
  { title: "Enrolment and USI", fields: [["studentEnrollmentId","Enrolment ID"],["studentBookingId","Booking ID"],["usiNumber","USI"],["usiExemptionReason","USI exemption reason"],["usiVerified","USI verified"],["enrolmentStatus","Status"],["progressPercentage","Progress %"],["currentStep","Current step"]] },
  { title: "Personal details", fields: [["title","Title"],["firstName","First name"],["middleName","Middle name"],["lastName","Last name"],["preferredName","Preferred name"],["genderId","Gender ID"],["dateOfBirth","Date of birth"],["email","Email"],["mobileNumber","Mobile"],["homePhone","Home phone"],["workPhone","Work phone"]] },
  { title: "Residential and postal address", fields: [["addressLine1","Address line 1"],["addressLine2","Address line 2"],["suburb","Suburb"],["cityId","City ID"],["stateId","State ID"],["countryId","Country ID"],["postCode","Postcode"],["sameAsResidential","Postal same as residential"],["postalAddressLine1","Postal address 1"],["postalAddressLine2","Postal address 2"],["postalSuburb","Postal suburb"],["postalPostCode","Postal postcode"]] },
  { title: "Emergency contact", fields: [["emergencyContactName","Contact name"],["emergencyContactRelationshipId","Relationship ID"],["emergencyPhone","Phone"]] },
  { title: "Language and cultural diversity", fields: [["countryOfBirthId","Country of birth ID"],["townOfBirth","Town of birth"],["australianCitizen","Australian citizen"],["indigenousStatus","Indigenous status"],["speakOtherLanguage","Speaks another language"],["otherLanguage","Other language"],["englishProficiency","English proficiency"]] },
  { title: "Schooling and education", fields: [["completedYear12","Completed Year 12"],["highestSchoolLevel","Highest school level"],["schoolCompletedYear","Year completed"],["ableToCompleteQualification","Able to complete qualification"],["qualificationCompletedYear","Qualification year"]] },
  { title: "Employment", fields: [["hasConcessionCard","Concession card"],["concessionCardNumber","Card number"],["employmentStatus","Employment status"],["occupation","Occupation"],["employerName","Employer"],["employmentStartDate","Start date"],["workingHoursPerWeek","Hours per week"]] },
  { title: "Disability and support", fields: [["disabilityFlag","Has disability"],["disabilitySupportRequired","Disability support"],["reasonableAdjustment","Reasonable adjustment"],["canReadWriteEnglish","Can read/write English"],["llnSupportRequired","LLN support"],["howDidYouHear","How did you hear about us"]] },
  { title: "Declaration and review", fields: [["declarationAccepted","Declaration accepted"],["declarationDate","Declaration date"],["studentSignature","Student signature"],["isSubmitted","Submitted"],["submittedDate","Submitted date"],["documentsVerified","Documents verified"],["adminComments","Admin comments"],["rejectedReason","Rejected reason"],["approvedDate","Approved date"],["rejectedDate","Rejected date"]] },
];

function EnrolmentDetailDialog({ detail, onClose }) {
  const data = detail.enrollment || {};
  const [statementNumber, setStatementNumber] = useState(data.statementOfAttainmentNumber || "");
  const [savingStatement, setSavingStatement] = useState(false);
  const [certificateForm, setCertificateForm] = useState({ certificateNumber: "", certificateName: detail.summary?.courseName ? `${detail.summary.courseName} Certificate` : "Certificate", issueDate: new Date().toISOString().slice(0, 10), expiryDate: "", remarks: "", file: null });
  const [savingCertificate, setSavingCertificate] = useState(false);
  const enrolmentStatus = Number(data.enrolmentStatus ?? detail.summary?.enrolmentStatus);
  const canEditStatement = [EnrollmentStatus.Completed, EnrollmentStatus.CertificateIssued].includes(enrolmentStatus);
  const canIssueCertificate = enrolmentStatus === EnrollmentStatus.Completed;
  const saveStatementNumber = async () => {
    setSavingStatement(true);
    try {
      const response = await adminEnrolmentService.updateStatementOfAttainmentNumber(
        data.studentEnrollmentId,
        statementNumber.trim() || null,
      );
      data.statementOfAttainmentNumber = response.statementOfAttainmentNumber || "";
      setStatementNumber(data.statementOfAttainmentNumber);
      ToastHelper.success(response.message || "Statement of Attainment Number saved.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save Statement of Attainment Number.");
    } finally {
      setSavingStatement(false);
    }
  };
  const issueCertificate = async () => {
    if (!certificateForm.certificateNumber.trim() || !certificateForm.certificateName.trim() || !certificateForm.issueDate || !certificateForm.file) {
      ToastHelper.error("Certificate number, name, issue date and PDF are required.");
      return;
    }
    const formData = new FormData();
    formData.append("EnrollmentId", String(data.studentEnrollmentId ?? detail.summary?.studentEnrollmentId));
    formData.append("CertificateNumber", certificateForm.certificateNumber.trim());
    formData.append("CertificateName", certificateForm.certificateName.trim());
    formData.append("IssueDate", new Date(`${certificateForm.issueDate}T00:00:00`).toISOString());
    if (certificateForm.expiryDate) formData.append("ExpiryDate", new Date(`${certificateForm.expiryDate}T00:00:00`).toISOString());
    if (statementNumber.trim()) formData.append("StatementOfAttainmentNumber", statementNumber.trim());
    if (certificateForm.remarks.trim()) formData.append("Remarks", certificateForm.remarks.trim());
    formData.append("File", certificateForm.file);
    setSavingCertificate(true);
    try {
      const response = await certificateService.create(formData);
      data.enrolmentStatus = EnrollmentStatus.CertificateIssued;
      detail.summary.enrolmentStatus = EnrollmentStatus.CertificateIssued;
      ToastHelper.success(response?.message || "Certificate issued successfully.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to issue certificate.");
    } finally {
      setSavingCertificate(false);
    }
  };
  const updateCertificateField = (key, value) => setCertificateForm((current) => ({ ...current, [key]: value }));
  const display = (value) => {
    if (value === true) return "Yes"; if (value === false) return "No";
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return new Date(value).toLocaleDateString("en-AU");
    if (Array.isArray(value)) return value.map(x => typeof x === "object" ? JSON.stringify(x) : x).join(", ") || "—";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };
  const documents = detail.documents || detail.studentDocuments || [];
  const studentEnrollmentId =
    data.studentEnrollmentId ?? data.StudentEnrollmentId ?? detail.summary?.studentEnrollmentId;
  const viewDocument = (document) =>
    adminEnrolmentService.viewDocument(
      studentEnrollmentId,
      document.studentDocId ?? document.StudentDocId,
    );
  const downloadDocument = (document) =>
    adminEnrolmentService.downloadDocument(
      studentEnrollmentId,
      document.studentDocId ?? document.StudentDocId,
    );
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={e=>e.target===e.currentTarget&&onClose()}><div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-slate-50 shadow-2xl"><div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4"><div><h2 className="text-xl font-display font-extrabold text-[#0A2540]">Enrolment details</h2><p className="text-sm text-slate-500">{detail.summary.studentName} · {detail.summary.courseName} · {detail.summary.bookingNumber}</p></div><button className="text-2xl text-slate-400" onClick={onClose}>×</button></div><div className="space-y-5 p-6"><section className="wc-card"><h3 className="mb-4 border-b pb-3 text-lg font-display font-extrabold text-[#0A2540]">Statement of Attainment</h3><div className="flex flex-col gap-3 sm:flex-row sm:items-end"><label className="flex-1"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Statement of Attainment Number</span><input className="wc-input" maxLength={100} value={statementNumber} onChange={e=>setStatementNumber(e.target.value)} readOnly={!canEditStatement} placeholder="Not assigned" /></label><button className="wc-btn-primary" disabled={!canEditStatement || savingStatement} onClick={saveStatementNumber}>{savingStatement ? "Saving..." : "Save number"}</button></div>{!canEditStatement && <p className="mt-2 text-xs text-amber-700">This number becomes editable after the course status is Completed.</p>}</section>{canIssueCertificate && <section className="wc-card"><h3 className="mb-4 border-b pb-3 text-lg font-display font-extrabold text-[#0A2540]">Issue Certificate</h3><div className="grid gap-4 sm:grid-cols-2"><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate number</span><input className="wc-input" value={certificateForm.certificateNumber} onChange={e=>updateCertificateField("certificateNumber",e.target.value)} /></label><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate name</span><input className="wc-input" value={certificateForm.certificateName} onChange={e=>updateCertificateField("certificateName",e.target.value)} /></label><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Issue date</span><input type="date" className="wc-input" value={certificateForm.issueDate} onChange={e=>updateCertificateField("issueDate",e.target.value)} /></label><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Expiry date</span><input type="date" className="wc-input" value={certificateForm.expiryDate} onChange={e=>updateCertificateField("expiryDate",e.target.value)} /></label><label className="sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate PDF</span><input type="file" accept="application/pdf,.pdf" className="wc-input" onChange={e=>updateCertificateField("file",e.target.files?.[0] || null)} /></label><label className="sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Remarks</span><textarea className="wc-input min-h-20" value={certificateForm.remarks} onChange={e=>updateCertificateField("remarks",e.target.value)} /></label></div><div className="mt-4 flex justify-end"><button className="wc-btn-primary" disabled={savingCertificate} onClick={issueCertificate}>{savingCertificate ? "Issuing..." : "Attach and issue certificate"}</button></div></section>}{DETAIL_SECTIONS.map(section=><section key={section.title} className="wc-card"><h3 className="mb-4 border-b pb-3 text-lg font-display font-extrabold text-[#0A2540]">{section.title}</h3><div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">{section.fields.map(([key,label])=><div key={key}><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 break-words text-sm text-slate-800">{display(data[key])}</div></div>)}</div></section>)}<section className="wc-card"><h3 className="mb-4 border-b pb-3 text-lg font-display font-extrabold text-[#0A2540]">My Documents</h3><StudentEnrollmentDocuments documents={documents} readOnly onViewDocument={viewDocument} onDownloadDocument={downloadDocument} /></section></div><div className="sticky bottom-0 flex justify-end border-t bg-white px-6 py-4"><button className="wc-btn-primary" onClick={onClose}>Close</button></div></div></div>;
}
